import { Box, Grid, Typography } from "@mui/material";
import {
  formatToIndianCurrencyWords2,
  mapHighestLevelToCategory,
  teamGradients,
} from "../../../../Utils/helperFunctions";
import {
  getAuctionRulesByDivision,
  normalizeCategoryForRules,
  resolveCategoryWithFallback,
  resolveTeamDivision,
} from "../../../../Utils/auctionRules";

const TeamSection: React.FC<any> = ({
  teamDetails,
  centerCards = false,
  showCategoryBreakdown = true,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        mt: { xs: 2.4, md: "1.65vw" },
        pt: { xs: 1.2, md: "1vw" },
      }}
    >
      <Grid
        container
        sx={{
          width: "100%",
          justifyContent: centerCards ? "center" : "flex-start",
          alignContent: "flex-start",
        }}
        columnSpacing={{ xs: 2, md: "2vw" }}
        rowSpacing={{ xs: 4.8, md: "2.35vw" }}
      >
        {teamDetails
          ?.slice()
          .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
          .map((item: any, i: number) => (
            <Grid
              item
              key={i}
              xs={12}
              sm={6}
              md={4}
              lg={3}
              sx={{ display: "flex" }}
            >
              <TeamCard
                team={item}
                showCategoryBreakdown={showCategoryBreakdown}
              />
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

const getTeamLogo = (team: any) => {
  const logo = team?.Logo_URL__c;
  return typeof logo === "string" && logo.trim() ? logo.trim() : "";
};

const TeamCard: React.FC<any> = ({ team, showCategoryBreakdown = true }) => {
  const players = team?.Player_Registrations__r?.records || [];
  const teamLogo = getTeamLogo(team);
  const gradient = teamGradients[team?.Name] || { from: "#1e1e1e", to: "#444" };
  const division = resolveTeamDivision(team);
  const rules = getAuctionRulesByDivision(division);
  const getCategoryCount = (categoryKey: string) =>
    players.filter((p: any) => {
      const mcaNo =
        p?.MCANo ||
        p?.MCA_No__c ||
        p?.MCA_Registration_Number__c ||
        p?.Player__r?.MCA_No__c ||
        p?.Player__r?.MCA_Registration_Number__c;
      const rawCategory = resolveCategoryWithFallback({
        division,
        mcaNo,
        fallbackCategory: mapHighestLevelToCategory(
          p?.Recent_Competitive_Level__c || p?.recentCompetetionLevel || "",
        ),
      });
      const normalizedCategory = normalizeCategoryForRules(rawCategory, rules, {
        playerAge: p?.playerAge || p?.Player_Age__c || p?.Age__c,
        birthData:
          p?.birthData ||
          p?.Birth_Date__c ||
          p?.Date_of_Birth__c ||
          p?.DOB__c ||
          null,
      });
      return normalizedCategory === categoryKey;
    }).length;
  const categoryCards =
    division === "women"
      ? [
          { label: "Icon", key: "Icon Player" },
          { label: "Under-19", key: "Under-19 Player" },
          { label: "General", key: "General Player" },
        ]
      : [
          { label: "Icon", key: "Icon Player" },
          { label: "Senior", key: "Senior Player" },
          { label: "Emerging", key: "Emerging Player" },
          { label: "Development", key: "Development Player" },
        ];
  const totalPlayers = players?.length;

  const remainingFundFormatted = formatToIndianCurrencyWords2(
    team?.Fund_Remaining__c,
  );

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: { xs: 1, md: "0.55vw" },
        px: { xs: 1.2, md: "0.5vw" },
        pb: showCategoryBreakdown
          ? { xs: 1.2, md: "0.55vw" }
          : { xs: 1.6, md: "0.85vw" },
        pt: { xs: 6.4, md: "4.15vw" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: `linear-gradient(to bottom, ${gradient.from}, ${gradient.to})`,
        minHeight: showCategoryBreakdown
          ? { xs: 238, md: "17.35vw" }
          : { xs: 188, md: "12.6vw" },
        width: "100%",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          zIndex: 3,
          textAlign: "center",
          top: { xs: -32, md: "-1.45vw" },
          backgroundColor: "white",
          p: { xs: 0.32, md: "0.18vw" },
          width: showCategoryBreakdown
            ? { xs: 104, md: "6.5vw" }
            : { xs: 118, md: "7.2vw" },
          height: showCategoryBreakdown
            ? { xs: 104, md: "6.5vw" }
            : { xs: 118, md: "7.2vw" },
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: "999px",
          overflow: "hidden",
        }}
      >
        {teamLogo ? (
          <img
            src={teamLogo}
            alt={team?.Name}
            width={100}
            height={100}
            style={{
              height: "100%",
              width: "100%",
              margin: "0 auto",
              objectFit: "contain",
            }}
          />
        ) : null}
      </Box>
      <Box
        sx={{
          textAlign: "center",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: showCategoryBreakdown
              ? { xs: "0.6rem", md: "0.72vw" }
              : { xs: "0.78rem", md: "0.88vw" },
            fontWeight: 600,
            mb: { xs: 0.25, md: "0.22vw" },
            mt: showCategoryBreakdown
              ? { xs: 2.3, md: "2.05vw" }
              : { xs: 4.1, md: "2.9vw" },
          }}
          color="white"
        >
          Fund remaining
        </Typography>
        <Typography
          variant="h1"
          sx={{
            fontSize: showCategoryBreakdown
              ? { xs: "1.65rem", md: "1.45vw" }
              : { xs: "2rem", md: "1.72vw" },
            mb: showCategoryBreakdown
              ? { xs: 1.8, md: "1.05vw" }
              : { xs: 0.45, md: "0.32vw" },
            fontWeight: 700,
            lineHeight: 1.08,
          }}
          color="white"
        >
          {remainingFundFormatted}
        </Typography>
        <Grid
          container
          spacing={{ xs: 0.55, md: "0.32vw" }}
          mt={
            showCategoryBreakdown
              ? { xs: 0.25, md: "1.8vw" }
              : { xs: "auto", md: "auto" }
          }
        >
          {showCategoryBreakdown &&
            categoryCards.map((item, index) => (
              <Grid item xs={6} key={index}>
                <Box
                  sx={{
                    bgcolor: "#FFFFFF36",
                    py: { xs: 0.35, md: "0.32vw" },
                    minHeight: { xs: 44, md: "2.25vw" },
                    width: "100%",
                    color: "#fff",
                    borderRadius: { xs: 0.6, md: "0.36vw" },
                    border: "0.5px solid #EAEAEA",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: "1rem", md: "0.95vw" },
                      fontWeight: 700,
                      mb: 0.1,
                    }}
                    color="white"
                  >
                    {getCategoryCount(item.key)}/
                    {rules.minCounts[item.key] || 0}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: "0.58rem", md: "0.48vw" },
                      fontWeight: 600,
                      py: { xs: 0.1, md: "0.08vw" },
                      background:
                        "linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.30) 50%, rgba(255, 255, 255, 0.00) 100%)",
                    }}
                    color="white"
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          <Grid item xs={12}>
            <Box
              sx={{
                bgcolor: "#FFFFFF36",
                py: showCategoryBreakdown
                  ? { xs: 0.35, md: "0.32vw" }
                  : { xs: 0.55, md: "0.45vw" },
                minHeight: showCategoryBreakdown
                  ? { xs: 44, md: "2.25vw" }
                  : { xs: 44, md: "2.35vw" },
                width: "100%",
                color: "#fff",
                borderRadius: { xs: 0.6, md: "0.36vw" },
                border: "0.5px solid #EAEAEA",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: showCategoryBreakdown
                    ? { xs: "1rem", md: "0.95vw" }
                    : { xs: "1.45rem", md: "1.35vw" },
                  fontWeight: 700,
                  mb: showCategoryBreakdown ? 0.1 : 0.18,
                }}
                color="white"
              >
                {showCategoryBreakdown
                  ? `${totalPlayers}/${rules.squadMin}`
                  : totalPlayers}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontSize: showCategoryBreakdown
                    ? { xs: "0.58rem", md: "0.5vw" }
                    : { xs: "0.78rem", md: "0.7vw" },
                  fontWeight: 600,
                  py: { xs: 0.1, md: "0.08vw" },
                }}
                color="white"
              >
                {showCategoryBreakdown ? "Total Players" : "Players bought"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TeamSection;
